/**
 * The collapsed tool calls shown above an agent reply.
 *
 * Each row names the tool in human terms and, when expanded, shows the raw
 * result. The raw JSON matters: when the agent does something surprising,
 * this is the only place to see what it actually passed and got back.
 */

import { cn } from "@/lib/utils"
import {
  Wrench, CheckCircle2, AlertCircle, Loader2, ChevronDown, ChevronRight,
} from "lucide-react"

export interface ToolExecution {
  id: string
  toolName: string
  args: Record<string, any>
  status: "preparing" | "executing" | "complete" | "error"
  result?: any
  error?: string
}

export interface ToolExecutionListProps {
  executions: ToolExecution[]
  /** Ids currently expanded. */
  expanded: Set<string>
  onToggle: (id: string) => void
  /**
   * Friendly name for a tool id.
   *
   * Passed in rather than imported, so this component does not need to know
   * about every backend's label map -- the page already merges them.
   */
  labelFor: (toolName: string) => string
}

/** `key=value, key=value` — enough to see what the call actually did. */
function summariseArgs(args: Record<string, unknown>): string {
  return Object.entries(args)
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => `${k}=${JSON.stringify(v)}`)
    .join(", ")
}

export function ToolExecutionList({
  executions, expanded, onToggle, labelFor,
}: ToolExecutionListProps) {
  if (!executions.length) return null

  return (
    <div className="space-y-1">
      {executions.map(exec => {
        const isOpen = expanded.has(exec.id)
        return (
          <div key={exec.id} className="overflow-hidden rounded-lg border bg-muted/50 text-xs">
            <button
              className="flex w-full items-center gap-2 px-3 py-1.5 transition-colors hover:bg-muted"
              onClick={() => onToggle(exec.id)}
            >
              {exec.status === "complete" ? (
                <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-500" />
              ) : exec.status === "error" ? (
                <AlertCircle className="h-3 w-3 shrink-0 text-destructive" />
              ) : (
                <Loader2 className="h-3 w-3 shrink-0 animate-spin text-blue-500" />
              )}
              <Wrench className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="font-medium text-foreground">{labelFor(exec.toolName)}</span>
              <span className="flex-1 truncate text-left text-muted-foreground">
                {exec.status === "preparing"
                  ? <span className="italic opacity-60">preparing…</span>
                  : summariseArgs(exec.args)}
              </span>
              {isOpen
                ? <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
                : <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />}
            </button>

            {isOpen && exec.result !== undefined && (
              <div className="border-t bg-background px-3 py-2">
                <pre className={cn(
                  "max-h-48 overflow-x-auto text-[10px] whitespace-pre-wrap",
                  "text-muted-foreground",
                )}>
                  {JSON.stringify(exec.result, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
